import { ArchiveBoxIcon, BellAlertIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { memo } from "react";

export interface IPackingAlertsBannerProps {
  /** Orders waiting to be packed for the selected clinic. */
  pendingCount: number;
  /** Orders received in the current announcement window (0 = no announcement). */
  announcementCount: number;
  /** Duration of the announcement window, used for the countdown bar. */
  announcementDurationMs: number;
  /** Opens the OMS Confirmed tab. */
  onClick: () => void;
}

// Same words however many orders land in the 10s window; the flash and countdown replay per order.
const ANNOUNCEMENT_TEXT = "Order received · Rider reaching soon";

/**
 * Navbar packing-alerts box for clinic_ops users, shaped like the header search
 * bar (rounded-md, text-xs) with a tinted body, a matching border and a solid
 * left segment so it stands out in the navbar. The Header shows it in place of
 * Customer / Calendar / user name only while something is pending packing or
 * an announcement is active:
 * - pending:    amber border + count segment + "orders pending packing · Pack ASAP"
 * - announcing: red border + bell segment + "Order received · Rider reaching soon"
 *               (no count) + 10s countdown
 * The whole box is clickable (opens the OMS Confirmed tab).
 */
const PackingAlertsBanner: React.FC<IPackingAlertsBannerProps> = ({
  pendingCount,
  announcementCount,
  announcementDurationMs,
  onClick,
}) => {
  const isAnnouncing = announcementCount > 0;
  const orderLabel = pendingCount === 1 ? "order" : "orders";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={false}
      animate={isAnnouncing ? { scale: [1, 1.02, 1] } : { scale: 1 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      title={`${pendingCount} ${orderLabel} pending packing — open Confirmed orders`}
      aria-live="polite"
      className={clsx(
        "group relative flex items-stretch w-full rounded-md overflow-hidden text-left text-xs shadow-sm",
        "ring-1 transition-colors duration-300 focus:outline-none focus-visible:ring-2",
        isAnnouncing
          ? "bg-red-50 ring-red-400 focus-visible:ring-red-500"
          : "bg-amber-50 ring-amber-400 hover:ring-amber-500 focus-visible:ring-amber-500",
      )}
    >
      {/* Solid segment the eye lands on first: the count, or a bell while announcing. */}
      <span
        className={clsx(
          "inline-flex items-center gap-x-1.5 px-3 py-2 font-bold tabular-nums shrink-0 transition-colors duration-300",
          isAnnouncing ? "bg-red-600 text-white" : "bg-amber-400 text-gray-900",
        )}
      >
        {isAnnouncing ? (
          <BellAlertIcon className="w-4 h-4 animate-pulse" aria-hidden="true" />
        ) : (
          <>
            <ArchiveBoxIcon className="w-4 h-4" aria-hidden="true" />
            <span className="text-sm leading-4">{pendingCount}</span>
          </>
        )}
      </span>

      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={isAnnouncing ? "announce" : "pending"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
          className={clsx(
            "flex flex-1 items-center gap-x-1.5 px-3 py-2 min-w-0 whitespace-nowrap truncate",
            isAnnouncing ? "font-semibold text-red-700" : "font-medium text-amber-900",
          )}
        >
          {isAnnouncing ? (
            ANNOUNCEMENT_TEXT
          ) : (
            <>
              <span className="truncate">{pendingCount === 1 ? "Order" : "Orders"} Pending</span>
              <span className="ml-auto pl-2 font-semibold text-amber-700 shrink-0">Pack Now</span>
            </>
          )}
        </motion.span>
      </AnimatePresence>

      <span className="flex items-center pr-3 shrink-0">
        <ChevronRightIcon
          className={clsx(
            "w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5",
            isAnnouncing ? "text-red-500" : "text-amber-600 group-hover:text-amber-800",
          )}
          aria-hidden="true"
        />
      </span>

      {isAnnouncing && (
        // Flash replays on every grouped arrival (re-keyed per order).
        <motion.span
          key={`flash-${announcementCount}`}
          aria-hidden="true"
          className="absolute inset-0 bg-red-400 pointer-events-none"
          initial={{ opacity: 0.35 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      )}

      {isAnnouncing && (
        // Countdown restarts whenever another order joins the 10s window.
        <motion.span
          key={`countdown-${announcementCount}`}
          aria-hidden="true"
          className="bottom-0 left-0 absolute bg-red-500 h-0.5"
          initial={{ width: "100%" }}
          animate={{ width: "0%" }}
          transition={{ duration: announcementDurationMs / 1000, ease: "linear" }}
        />
      )}
    </motion.button>
  );
};

export default memo(PackingAlertsBanner);
